import { chipInteractionHint } from '#components/chip-interaction-hint/chip-interaction-hint.module.css';

import { DrawingProperties } from '@cadstrom/renderer';
import { SchLayerType } from '@cadstrom/sigma-engine-shared';
import TagIcon from '@mui/icons-material/Tag';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { use, useCallback, useMemo, useState } from 'react';

import {
	CadstromColorProviderContext,
	graphicRenderOptsToCss,
	useColorForLayer,
} from '#components/cad-renderer/context/cadstrom-color-provider-context.js';
import { useCoreModelContext } from '#components/cad-renderer/context/core-model-context.js';
import { InteractionManagerContext } from '#components/cad-renderer/context/interaction-manager-context.js';
import {
	dismissChipInteractionHint,
	useChipInteractionHint,
} from '#components/chip-interaction-hint/chip-interaction-hint-store.js';
import { ChipInteractionHintTooltipContent } from '#components/chip-interaction-hint/chip-interaction-hint-tooltip-content.js';
import { ChipInteractionHint } from '#components/chip-interaction-hint/chip-interaction-hint.js';
import { ModelText } from '#components/model-text/model-text.js';
import { theme } from '#src/theme.js';
import { CadRendererSetActiveDesignEvent, CadRendererZoomEvent, dispatchEventAsync } from '#utils/events.js';
import { GroundIcon } from './ground-icon.js';
import { NetChipPopper } from './net-chip-popper.js';

import type { ModelWithNetStats } from '@cadstrom/sigma-engine-shared';
import type { ChipProps } from '@mui/material/Chip';

const GROUND_SIGMA_MODEL_ID = 'ground.standard';

export interface NetChipProps {
	net: string;
	selected?: boolean;
	highlighted?: boolean;
	xToDeselect?: boolean;
	className?: string;
	chipProps?: ChipProps;

	storybook_forceHoverState?: boolean;
	/**
	 * Overrides the attention pulse: `true` always pulses (with the instant tooltip), `false` never
	 * pulses. Leave undefined for the default one-chip-at-a-time behaviour.
	 */
	storybook_forceInteractionHint?: boolean;
}

export function NetChip({
	net,
	selected,
	highlighted,
	xToDeselect,
	className,
	chipProps,
	storybook_forceHoverState,
	storybook_forceInteractionHint,
}: NetChipProps) {
	const [popperAnchor, setPopperAnchor] = useState<{ anchorEl: HTMLElement } | false>(false);
	const forceSelected = selected;
	const forceHighlighted = highlighted;

	const interactionManagerContext = use(InteractionManagerContext);
	const { coreModel } = useCoreModelContext(false) ?? {};
	const colorProviders = use(CadstromColorProviderContext);

	const wireColorOpts = useColorForLayer(colorProviders?.schematic, SchLayerType.Wire);
	const wireColor = wireColorOpts ? graphicRenderOptsToCss(wireColorOpts.selectionNeutral) : '#444444';

	const isGroundNet = useMemo(() => (coreModel ? netHasGroundComponent(coreModel, net) : false), [coreModel, net]);

	// Check if this net is in the highlight map
	const isHighlightedByInteractionManager =
		interactionManagerContext?.highlight.get(DrawingProperties.Net)?.has(net) ?? false;

	// Check if this net is in the selection map
	const isSelectedByInteractionManager =
		interactionManagerContext?.selection.get(DrawingProperties.Net)?.has(net) ?? false;

	const setHighlight = useCallback(
		(net: string, isHovered: boolean) => {
			const highlightMap = new Map();
			if (isHovered) {
				highlightMap.set(DrawingProperties.Net, new Set([net]));
			}
			interactionManagerContext?.setHighlight(highlightMap);
		},
		[interactionManagerContext],
	);

	const setSelection = useCallback(
		(net: string, isSelected: boolean) => {
			const selectionMap = new Map();
			if (isSelected) {
				selectionMap.set(DrawingProperties.Net, new Set([net]));
			}
			interactionManagerContext?.setSelection(selectionMap);
		},
		[interactionManagerContext],
	);

	const handleZoomToItem = useCallback(async (net: string) => {
		await dispatchEventAsync(new CadRendererSetActiveDesignEvent({ net }));
		await dispatchEventAsync(new CadRendererZoomEvent({ net }));
	}, []);

	const handleMouseEnter = useCallback(() => {
		setHighlight(net, true);
	}, [setHighlight, net]);

	const handleMouseLeave = useCallback(() => {
		setHighlight(net, false);
	}, [setHighlight, net]);

	const handleClick = useCallback(() => {
		dismissChipInteractionHint();
		setSelection(net, true);
		void handleZoomToItem(net);
	}, [net, setSelection, handleZoomToItem]);

	const handleDelete = useCallback(() => {
		setSelection(net, false);
	}, [setSelection, net]);

	const handleContextMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
		dismissChipInteractionHint();
		setPopperAnchor((prev) => (prev !== false ? false : { anchorEl: e.currentTarget }));
	}, []);

	const isSelected = forceSelected === true || isSelectedByInteractionManager;
	const isHighlighted = forceHighlighted === true || isHighlightedByInteractionManager;

	const { pulse, observe: observeChipForHint } = useChipInteractionHint({
		eligible: storybook_forceInteractionHint !== false && chipProps?.disabled !== true && !isSelected,
	});
	const hintPulse = storybook_forceInteractionHint ?? pulse;

	return (
		<>
			<Tooltip
				title={<ChipInteractionHintTooltipContent />}
				enterDelay={hintPulse ? 0 : 1000}
				enterNextDelay={hintPulse ? 0 : 1000}
				disableInteractive
			>
				<span className={clsx('relative leading-0', className)} style={{ '--chip-color': wireColor }}>
					<Chip
						component='button'
						type='button'
						{...chipProps}
						ref={observeChipForHint}
						label={<ModelText value={net} />}
						variant={isSelected || isHighlighted ? 'filled' : 'outlined'}
						icon={isGroundNet ? <GroundIcon className='ml-1.25' /> : <TagIcon className='-mr-0.5 ml-1' />}
						data-force-hover-state={storybook_forceHoverState}
						className={clsx(
							(isSelected || isHighlighted) &&
								'shadow-[-0.5px_1px_6px_oklch(from_var(--chip-shadow-color)_l_c_h/calc(alpha*0.6))]',
							hintPulse && !isHighlighted && chipInteractionHint,
							chipProps?.className,
						)}
						classes={{
							...chipProps?.classes,
							outlined: clsx(
								'text-(--chip-color)',
								'dark:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] dark:bg-[oklch(from_var(--chip-color)_l_c_h/0.2)]',
								'dark:hover:border-[oklch(from_var(--chip-color)_l_c_h/0.6)] dark:hover:bg-[oklch(from_var(--chip-color)_l_c_h/0.3)]',
								'light:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] light:bg-[oklch(from_var(--chip-color)_l_c_h/0.1)]',
								'light:hover:border-[oklch(from_var(--chip-color)_l_c_h/0.5)] light:hover:bg-[oklch(from_var(--chip-color)_l_c_h/0.15)]',
								chipProps?.classes?.outlined,
							),
							filled: clsx(
								'dark:border-[oklch(from_var(--chip-color)_calc(l*0.8)_calc(c*0.8)_h/0.7)] dark:bg-[oklch(from_var(--chip-color)_l_c_h)]',
								'dark:hover:border-[oklch(from_var(--chip-color)_calc(l*0.8)_calc(c*0.8)_h/0.8)] dark:hover:bg-[oklch(from_var(--chip-color)_calc(l*1.1)_c_h)]',
								'light:border-[color-mix(in_oklab,var(--chip-color),white_40%)] light:bg-(--chip-color)',
								'light:hover:border-[color-mix(in_oklab,var(--chip-color),white_20%)] light:hover:bg-[oklch(from_var(--chip-color)_calc(l*1.1)_c_h/0.9)]',
								chipProps?.classes?.filled,
							),
						}}
						sx={[
							{
								'&.MuiChip-filled': {
									color: theme.palette.getContrastText(wireColor),
									'--text-shadow-color': 'oklch(from currentcolor calc((l - 0.5) * 0.7 + 0.5) 0 h / 0.2)',
								},
							},
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							...(Array.isArray(chipProps?.sx) ? chipProps.sx : [chipProps?.sx]),
						]}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						onClick={handleClick}
						onContextMenu={handleContextMenu}
						onDelete={xToDeselect ? handleDelete : undefined}
					/>
					{hintPulse && !isHighlighted && <ChipInteractionHint />}
				</span>
			</Tooltip>

			{popperAnchor !== false && (
				<NetChipPopper net={net} anchorEl={popperAnchor.anchorEl} onClose={() => setPopperAnchor(false)} />
			)}
		</>
	);
}

/**
 * Returns whether any component connected to the named net resolves to the `ground.standard`
 * Sigma model. The net is matched by name, local name, or net label across all designs.
 *
 * @param coreModel - The project core model to search.
 * @param netName - The net name the chip displays.
 * @returns `true` when at least one connected component has the ground Sigma model.
 */
function netHasGroundComponent(coreModel: ModelWithNetStats, netName: string): boolean {
	for (const design of Object.values(coreModel.designs)) {
		for (const net of Object.values(design.nets)) {
			if (net.name !== netName && net.localName !== netName && !net.netLabels?.some((l) => l.label === netName)) {
				continue;
			}

			for (const connection of net.connections) {
				const designModule = design.modules.find((m) => m.ref === connection.moduleRef);
				if (!designModule) {
					continue;
				}

				const projectModuleName = designModule.fullModuleName ?? designModule.name;
				const projectModule = coreModel.modules.find((m) => m.name === projectModuleName);
				if (projectModule?.model?.modelId === GROUND_SIGMA_MODEL_ID) {
					return true;
				}
			}
		}
	}

	return false;
}
